// src/elements/hudReel/hudReelWakeZone.ts
import { log } from "../../utils/debug";
import { Z } from "../../utils/constants";
const HOT_WHEN_HIDDEN_SEL = '[data-hot-when-hidden], .like-button';

export interface WakeZoneHandle {
  el: HTMLElement;
  destroy: () => void;
}

type WakeOpts = {
  headerHeightPx?: number; // default 56
  wakeShowMs?: number;     // default 5000
  parent?: HTMLElement | null;
  debug?: boolean;         // draw outline / log events
};

/** Creates a transparent wake zone over the reel to reveal HUD on tap. */
export function createWakeZone(
  section: HTMLElement,
  controls: HTMLElement,
  video: HTMLVideoElement,
  show: (ms: number, flashOverlay?: boolean) => void,
  tapMoveThresholdPx: number,
  tapMaxMs: number,
  opts?: WakeOpts
): WakeZoneHandle {
  const headerHeightPx = Math.max(0, opts?.headerHeightPx ?? 56);
  const wakeShowMs = Math.max(0, opts?.wakeShowMs ?? 5000);
  const parent: HTMLElement = (opts?.parent ?? overlayParent(video) ?? section) as HTMLElement;
  const debug = !!opts?.debug || (window as any).__SC_WAKE_DEBUG === true;

  // Idempotency: reuse existing zone if present
  const existing = parent.querySelector<HTMLElement>(".reel-wake-zone");
  if (existing) {
    // If toggling debug on an existing zone, update visuals
    if (debug) decorateDebug(existing, true);
    else decorateDebug(existing, false);
    return { el: existing, destroy: () => { existing.remove(); } };
  }

  const wakeZone = document.createElement("div");
  wakeZone.className = "reel-wake-zone";

  // ✅ Box sizing & positioning
  wakeZone.style.position = "absolute";
  wakeZone.style.left = "0";
  wakeZone.style.right = "0";
  wakeZone.style.top = `${headerHeightPx}px`;
  wakeZone.style.bottom = "0";
  // (top/bottom wins over height calc; keep both for safety if CSS overrides)
  wakeZone.style.height = `calc(100% - ${headerHeightPx}px)`;

  // Start active for pointer events; we’ll disable dynamically when HUD is shown
  wakeZone.style.pointerEvents = "auto";
  wakeZone.style.touchAction = "pan-y";
  wakeZone.style.background = "transparent";

  // Place just under HUD buttons so it doesn't block them
  wakeZone.style.zIndex = String(Z.HUD - 1);

  // a11y (optional)
  wakeZone.setAttribute("role", "button");
  wakeZone.setAttribute("aria-label", "Show video controls");

  let downX = 0, downY = 0, downT = 0;

  const onPointerDownZone = (e: PointerEvent) => {
    downX = e.clientX;
    downY = e.clientY;
    downT = Date.now();
    if (debug) log("[wake] pointerdown", { x: downX, y: downY, t: downT });
  };

  const forceWake = () => {
    if (debug) log("[wake] forceWake → show HUD, disable zone temporarily");
    (window as any).__SC_HUD_WAKING = true;
    show(wakeShowMs, true);
    setTimeout(() => { (window as any).__SC_HUD_WAKING = false; }, 0);

    // 🚫 Temporarily disable wake-zone while HUD is visible
    wakeZone.style.pointerEvents = "none";
    // Re-enable on hud-hide event
    controls.addEventListener("sc:hud-hide", () => {
      wakeZone.style.pointerEvents = "auto";
      if (debug) log("[wake] re-enabled on hud-hide");
      updateDebugBadge(wakeZone);
    }, { once: true });

    updateDebugBadge(wakeZone);
  };

  const onPointerUpZone = (e: PointerEvent) => {
    const dx = Math.abs(e.clientX - downX);
    const dy = Math.abs(e.clientY - downY);
    const dt = Date.now() - downT;
    const isTap = dx < tapMoveThresholdPx && dy < tapMoveThresholdPx && dt < tapMaxMs;
    if (debug) log("[wake] pointerup", { dx, dy, dt, isTap });
    if (isTap) forceWake();
  };

  // If HUD is hidden, consume the first pointerdown and wake it
  const ensureVisibleOnPointerDown = (ev: Event) => {
    const visible = controls.classList.contains("hud-active");
    const t = ev.target as Element | null;
    const hot = !!t && !!(t.closest?.(HOT_WHEN_HIDDEN_SEL));

    if (!visible) {
      if (hot) {
        // Allow immediate interaction for hot targets, but also wake the HUD
        if (debug) log("[wake] hot tap while hidden → allow + wake");
        try { forceWake(); } catch {}
        // IMPORTANT: do NOT preventDefault/stopPropagation so the click reaches the control
        return;
      }
      // Normal wake behavior: consume the first tap
      if (debug) log("[wake] HUD inactive, waking (consuming tap)");
      ev.stopPropagation();
      (ev as any).preventDefault?.(); // listener is capture + non-passive
      forceWake();
      return;
    }

    // If HUD is visible, never let wakeZone block HUD buttons
    wakeZone.style.pointerEvents = "none";
  };

  // Attach listeners
  wakeZone.addEventListener("pointerdown", onPointerDownZone, { passive: true });
  wakeZone.addEventListener("pointerup", onPointerUpZone, { passive: true });
  // Non-passive/capture so preventDefault works before media/controls see it
  video.addEventListener("pointerdown", ensureVisibleOnPointerDown, true);
  controls.addEventListener("pointerdown", ensureVisibleOnPointerDown, true);

  // Visualize / debug
  decorateDebug(wakeZone, debug);

  parent.appendChild(wakeZone);

  const destroy = () => {
    wakeZone.removeEventListener("pointerdown", onPointerDownZone);
    wakeZone.removeEventListener("pointerup", onPointerUpZone);
    video.removeEventListener("pointerdown", ensureVisibleOnPointerDown as any, true);
    controls.removeEventListener("pointerdown", ensureVisibleOnPointerDown as any, true);
    wakeZone.remove();
  };

  return { el: wakeZone, destroy };
}

function overlayParent(video: HTMLVideoElement): HTMLElement | null {
  return video.parentElement;
}

/* ───────────────────────── DEBUG HELPERS ───────────────────────── */

function decorateDebug(el: HTMLElement, on: boolean) {
  if (!on) {
    el.removeAttribute("data-wake-debug");
    const badge = el.querySelector(".wake-badge");
    if (badge) badge.remove();
    el.style.outline = "";
    return;
  }
  el.setAttribute("data-wake-debug", "1");
  el.style.outline = "2px dashed rgba(0,200,255,.6)";

  // badge
  let badge = el.querySelector<HTMLDivElement>(".wake-badge");
  if (!badge) {
    badge = document.createElement("div");
    badge.className = "wake-badge";
    badge.style.position = "absolute";
    badge.style.top = "-10px";
    badge.style.right = "-10px";
    badge.style.font = "11px/1.2 monospace";
    badge.style.background = "rgba(0,0,0,.75)";
    badge.style.color = "#fff";
    badge.style.padding = "2px 4px";
    badge.style.borderRadius = "3px";
    badge.style.pointerEvents = "none";
    badge.style.zIndex = "999999";
    el.appendChild(badge);
  }
  updateDebugBadge(el);
}

function updateDebugBadge(el: HTMLElement) {
  const badge = el.querySelector<HTMLDivElement>(".wake-badge");
  if (!badge) return;
  const z = getComputedStyle(el).zIndex;
  const pe = getComputedStyle(el).pointerEvents;
  badge.textContent = `wake z:${z || "auto"} pe:${pe}`;
}